import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router';

import { DownloadButton } from '../../components/DownloadButton';
import { Spinner } from '../../components/Spinner';
import { StatusBadge } from '../../components/StatusBadge';
import { useEntitlements } from '../../contexts/entitlementsContext';
import { useError } from '../../contexts/useError';
import { useBowser } from '../../hooks/useBowser';
import { formatFileSize } from '../../utils/downloadUtils';
import {
  getLatestVersionFromArray,
  getVersionProperty,
} from '../../utils/entitlementUtils';
import { getErrorStatusCode } from '../../utils/errorUtils';

import { DownloadDetailedHeader } from './components/DownloadDetailedHeader';
import { DownloadDetailedInfo } from './components/DownloadDetailedInfo';
import { DownloadDetailedReleaseNotes } from './components/DownloadDetailedReleaseNotes';
import { DownloadDetailedSidebar } from './components/DownloadDetailedSidebar';
import { DownloadDetailedTable } from './components/DownloadDetailedTable';
import './DownloadDetailedPage.scss';

export const DownloadDetailedPage = () => {
  const { artifactKey } = useParams<{ artifactKey: string }>();
  const { isMobile } = useBowser();
  const { entitlements, error, isError, isLoading } = useEntitlements();
  const { setError } = useError();

  // set error context if error detected
  useEffect(() => {
    if (isError && error) {
      const statusCode = getErrorStatusCode(error);

      setError({ statusCode });
    }
  }, [isError, error, setError]);

  if (isLoading) {
    return (
      <div className="full-page-container">
        <div className="download-detailed-page__status__loading">
          <Spinner text="" />
        </div>
      </div>
    );
  }

  const artifact = entitlements?.artifacts.find(
    ({ artifactKey: key }) => key === artifactKey,
  );

  if (!artifact || !artifactKey) {
    return <Navigate to="/" replace />;
  }

  const { artifactDescription, artifactName, versions } = artifact;

  // Derive latest release and last published from versions (pre-sorted)
  const latestVersion = getLatestVersionFromArray(versions);
  const latestRelease = getVersionProperty(latestVersion, 'version');

  const tableData = versions.map(
    ({ version, fileSize, status, releaseDate }) => (
      <tr className="download-detailed-table__body-row" key={version}>
        <th
          scope="row"
          className="download-detailed-table__body-row-text download-detailed-table__body-row-text-version"
        >
          {version}
        </th>
        <td className="download-detailed-table__body-row-text">
          <StatusBadge status={status} />
        </td>
        <td className="download-detailed-table__body-row-text">
          {releaseDate}
        </td>
        <td className="download-detailed-table__body-row-text">
          {formatFileSize(fileSize)}
        </td>
        {!isMobile && (
          <td className="download-detailed-table__body-row-text download-detailed-table__body-row-text-download">
            <DownloadButton
              artifactKey={artifactKey}
              buttonText={'Download'}
              version={version}
              ariaLabel={`Download version ${version}`}
            />
          </td>
        )}
      </tr>
    ),
  );

  return (
    <div className="download-detailed-page">
      <DownloadDetailedSidebar currentArtifactKey={artifactKey} />
      <div className="download-detailed__container">
        <DownloadDetailedHeader
          artifact={artifactName}
          description={artifactDescription}
          isMobile={isMobile}
        />
        <div className="download-detailed__info">
          <div className="download-detailed__info-left">
            <DownloadDetailedTable
              artifactName={artifactName}
              tableData={tableData}
            />
          </div>
          <div className="download-detailed__info-right">
            <DownloadDetailedInfo latestRelease={latestRelease} />
          </div>
        </div>
        <DownloadDetailedReleaseNotes releaseNotes={versions} />
      </div>
    </div>
  );
};
